// Third-party
import React from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';

// Components
import AccessibilityDescription from '../../misc/AccessibilityDescription';
import { ColGroup } from './ColGroup';
import CorrelationLegend from '../../../../../views/components/charts/correlation-chart/CorrelationLegend';

// Types
import ProposalTableCellData from '@/types/proposal-table-cell-data';
import ProposalTableRowData from '@/types/proposal-table-row-data';
import ComparisonItem from '@/types/comparison-item';
import { ORIENTATION } from '@/types/proposal';
import ProposalTableProps from '@/types/proposal-table';

// Utils
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { LOCALE } from '@/utils/i18n';
import { Header } from './Header';
import { BodyRow } from './BodyRow';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';

const ProposalTable: React.FC<ProposalTableProps> = React.memo(props => {
  const {
    tableData: { rows, header, colgroup, caption },
    className,
    accessibilityDescription,
    locale,
    toolbarOption,
    tableOption = {},
    proposalItems,
    user,
  } = props;
  const id = React.useMemo(() => uniqueId('proposal-table-'), []);

  const hasAccessibilityPermission = user ? userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_ACCESSIBILITY) : false;

  const validateFilteredItem = (item: ProposalTableCellData) =>
    item.className !== 'hidden' &&
    (item.itemType === COMPARISON_ITEM_TYPES.FUND ||
      item.itemType === COMPARISON_ITEM_TYPES.STOCK ||
      item.itemType === COMPARISON_ITEM_TYPES.BENCHMARK ||
      item.itemType === COMPARISON_ITEM_TYPES.PORTFOLIO ||
      item.itemType === COMPARISON_ITEM_TYPES.GROUP);

  const getDataItems = (header?: ProposalTableRowData, rows?: ProposalTableRowData[]) => {
    let items: string[] = [];

    if (rows?.length && tableOption?.orientation === ORIENTATION.HORIZONTAL) {
      items = (rows.filter(rowItem => validateFilteredItem(rowItem.data[0])).map(({ data }) => data[0].value) as string[]) || [];
    }

    if (header && Object.keys(header).length > 0 && tableOption?.orientation !== ORIENTATION.HORIZONTAL) {
      items = (header?.data?.filter((item: ProposalTableCellData) => validateFilteredItem(item)).map(({ value }) => value) as string[]) || [];
    }

    if (!items?.length) {
      items =
        (proposalItems
          ?.filter((item: ComparisonItem) => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO)
          .map(({ name }) => name[locale || LOCALE.EN_CA]) as string[]) || [];
    }

    return items;
  };

  let showHeader: boolean = true;
  if (tableOption && tableOption.showHeader !== undefined) {
    // eslint-disable-next-line prefer-destructuring
    showHeader = tableOption.showHeader;
  }
  const isCorrelationMatrix = className?.includes('COMPARISON_CORRELATION_MATRIX');
  const isPortfolioCorrelationMatrix = !isCorrelationMatrix && className?.includes('CORRELATION_MATRIX');

  const renderTableHeader = () => {
    if (!header || !showHeader) return null;

    const headerComponent = <Header tableId={id} proposalTableRowData={header} locale={locale} hasAccessibilityPermission={hasAccessibilityPermission} />;

    return <thead>{headerComponent}</thead>;
  };

  const renderTableBody = () => {
    const bodyRows = rows.map((rowData, rowIndex) => {
      const colspan = rowData.data.reduce((total, cell) => total + (cell?.colSpan ?? 1), 0);

      const getGroupHeaderRowIndex = (currentRowIndex: number) => {
        for (let i = currentRowIndex - 1; i >= 0; i--) {
          const isGroupHeader = hasAccessibilityPermission && rows[i]?.data[0]?.className?.includes('proposal-table-group');
          if (isGroupHeader) {
            return i;
          }
        }
        return undefined;
      };

      const groupHeaderRowIndex = getGroupHeaderRowIndex(rowIndex);

      return (
        <BodyRow
          tableId={id}
          key={rowIndex}
          proposalTableRowData={rowData}
          rowIndex={rowIndex}
          locale={locale}
          colspan={colspan}
          hasAccessibilityPermission={hasAccessibilityPermission}
          groupHeaderRowIndex={groupHeaderRowIndex}
        />
      );
    });

    return <tbody>{bodyRows}</tbody>;
  };

  const renderTableBodyWithFooterHeader = () => {
    const bodyRows = rows.slice(0, -1).map((rowData, rowIndex) => {
      // Ensure all rows have the same number of cells by padding with empty cells if necessary for a11y
      const emptyCellsNeeded = rows.length - rowData.data.length;
      if (emptyCellsNeeded > 0) {
        const paddedRowData = {
          ...rowData,
          data: [
            ...rowData.data,
            ...Array(emptyCellsNeeded)
              .fill(null)
              .map(() => ({
                value: '',
              })),
          ],
        };
        return <BodyRow key={rowIndex} proposalTableRowData={paddedRowData} rowIndex={rowIndex} locale={locale} />;
      }
      return <BodyRow key={rowIndex} proposalTableRowData={rowData} rowIndex={rowIndex} locale={locale} />;
    });

    const footerHeader = <Header proposalTableRowData={rows[rows.length - 1]} locale={locale} />;

    return (
      <>
        <tbody>{bodyRows}</tbody>
        {footerHeader}
      </>
    );
  };

  const getTableClasses = (hasHeaderAsFooter: boolean) =>
    classNames('module-table-container', {
      'no-thead': hasAccessibilityPermission && !hasHeaderAsFooter,
      'header-as-footer': hasHeaderAsFooter && hasAccessibilityPermission,
    });

  const renderTable = (hasHeaderAsFooter = false) => {
    const shouldUseFooterHeader = hasHeaderAsFooter && hasAccessibilityPermission;

    return (
      <table id={id} className={getTableClasses(hasHeaderAsFooter)}>
        {!!caption && <caption>{caption}</caption>}
        {!!colgroup && <ColGroup colgroup={colgroup} />}
        {renderTableHeader()}
        {shouldUseFooterHeader ? renderTableBodyWithFooterHeader() : renderTableBody()}
      </table>
    );
  };

  return (
    <div className={classNames('proposal-table', className)}>
      {accessibilityDescription && (
        <AccessibilityDescription description={accessibilityDescription} items={getDataItems(header, rows)} locale={locale} toolbarOption={toolbarOption} />
      )}

      {isCorrelationMatrix ? (
        <table>
          <tr>
            <td colSpan={11}>{renderTable()}</td>
            <td colSpan={1}>
              <div className="correlation-legend-pdf-container">
                <CorrelationLegend />
              </div>
            </td>
          </tr>
        </table>
      ) : isPortfolioCorrelationMatrix ? (
        renderTable(true)
      ) : (
        renderTable()
      )}
    </div>
  );
}, isEqual);

export default ProposalTable;
